import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { isIbCardV2Topic, groupIbSignalItems, IbEntryCardV2, IbUpdateCardV2 } from "@/components/signals/IbSignalCardV2";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { X, Send, ImagePlus, Trash2, Radio, ShieldCheck, Loader2, Check, ChevronDown, ChevronLeft, ChevronRight, AlertCircle, RotateCw, Pin, Pencil, Eye, Link2, BarChart3, Target } from "lucide-react";

const MEDIA_BASE = (api.defaults.baseURL || "").replace(/\/api\/?$/, "");
const mediaSrc = (u) => (!u ? null : u.startsWith("http") ? u : `${MEDIA_BASE}${u.startsWith("/") ? "" : "/"}${u}`);
const MYT_TZ = "Asia/Kuala_Lumpur";
const dateKey = (v) => { try { return new Date(v).toLocaleDateString("en-CA", { timeZone: MYT_TZ }); } catch { return ""; } };
const dateLabel = (v) => { try { return `${new Date(v).toLocaleDateString("en-GB", { timeZone: MYT_TZ, day: "2-digit", month: "short", year: "numeric" })} · MYT`; } catch { return ""; } };
const timeLabel = (v) => { try { return new Date(v).toLocaleTimeString("en-US", { timeZone: MYT_TZ, hour: "numeric", minute: "2-digit", hour12: true }); } catch { return ""; } };

const TYPE_META = {
  entry: { label: "ENTRY", cls: "text-sky-400 border-sky-400/30 bg-sky-400/10" },
  running: { label: "RUNNING", cls: "text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/10" },
  tp_hit: { label: "TP HIT", cls: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10" },
  sl_hit: { label: "SL HIT", cls: "text-red-400 border-red-400/30 bg-red-400/10" },
  cancelled: { label: "CANCELLED", cls: "text-[#94A3B8] border-white/10 bg-white/5" },
};
const COMPOSER_TYPES = [
  ["text", "TEXT", "text-white"],
  ["entry", "ENTRY", "text-sky-400"],
  ["running", "RUNNING", "text-[#D4AF37]"],
  ["tp_hit", "TP HIT", "text-emerald-400"],
  ["sl_hit", "SL HIT", "text-red-400"],
  ["cancelled", "CANCELLED", "text-[#94A3B8]"],
];
const ALLOWED = ["image/jpeg", "image/png", "image/webp"];

async function resizeImage(file, maxDim = 1600) {
  try {
    const bitmap = await createImageBitmap(file);
    let { width, height } = bitmap;
    if (Math.max(width, height) > maxDim) {
      const s = maxDim / Math.max(width, height);
      width = Math.round(width * s); height = Math.round(height * s);
    }
    const canvas = document.createElement("canvas");
    canvas.width = width; canvas.height = height;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
    const type = file.type === "image/png" ? "image/png" : file.type === "image/webp" ? "image/webp" : "image/jpeg";
    const blob = await new Promise((r) => canvas.toBlob(r, type, 0.9));
    if (!blob) return file;
    return new File([blob], file.name, { type });
  } catch {
    return file;
  }
}

function Avatar({ name, image, size = "h-9 w-9 text-[11px]" }) {
  const initials = (name || "IB").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  if (image) {
    return (
      <img
        src={image}
        alt={name || "Channel"}
        className={`${size} shrink-0 rounded-full object-cover border-2 border-[#D4AF37]/60 bg-[#171512]`}
        data-testid="signal-channel-avatar-image"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
    );
  }
  return (
    <div className={`${size} shrink-0 rounded-full bg-[#D4AF37]/15 border-2 border-[#D4AF37]/60 flex items-center justify-center font-semibold text-[#D4AF37]`} data-testid="signal-channel-avatar-initials">
      {initials}
    </div>
  );
}

function DateSeparator({ label }) {
  return (
    <div className="flex justify-center my-1">
      <span className="text-[10px] tracking-wide text-[#94A3B8] bg-white/[0.05] border border-white/10 rounded-full px-3 py-1">{label}</span>
    </div>
  );
}

function RecapStat({ label, value, cls, testid }) {
  return (
    <div className="flex flex-col items-center min-w-0">
      <div className={`text-base sm:text-lg font-bold leading-none truncate ${cls}`} data-testid={testid}>{value}</div>
      <div className="mt-1 text-[9px] uppercase tracking-wide text-[#6B7486]">{label}</div>
    </div>
  );
}

function RecapSection({ name, s }) {
  const wr = s.win_rate == null ? "N/A" : `${Math.round(s.win_rate * 100)}%`;
  const net = s.net_pips > 0 ? `+${s.net_pips.toFixed(1)}` : s.net_pips.toFixed(1);
  const netCls = s.net_pips > 0 ? "text-emerald-400" : s.net_pips < 0 ? "text-red-400" : "text-[#E8ECF4]";
  return (
    <div className="ibc-recap__section p-3" data-testid={`recap-section-${name}`}>
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-[11px] font-mono tracking-[0.2em] uppercase text-[#E6C458]">{name}</span>
        <span className="text-[10px] text-[#8A94A6]" data-testid={`recap-${name}-unresolved`}>{s.unresolved} open</span>
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        <RecapStat label="Wins" value={s.wins} cls="text-emerald-400" testid={`recap-${name}-wins`} />
        <RecapStat label="Losses" value={s.losses} cls="text-red-400" testid={`recap-${name}-losses`} />
        <RecapStat label="Win Rate" value={wr} cls="text-[#E8ECF4]" testid={`recap-${name}-winrate`} />
        <RecapStat label="Net Pips" value={net} cls={netCls} testid={`recap-${name}-netpips`} />
      </div>
    </div>
  );
}

function RecapCard({ data }) {
  const sec = data?.sections || {};
  return (
    <div className="ibc-recap w-full p-3.5" data-testid="signal-recap-card">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="h-4 w-4 text-[#E6C458]" />
        <span className="text-[13px] font-semibold tracking-[0.14em] uppercase text-gold-gradient">Daily Results</span>
        <span className="text-[10px] text-[#8A94A6] ml-auto" data-testid="recap-date">{data?.date} · MYT</span>
      </div>
      <div className="space-y-2.5">
        {sec.TZB && <RecapSection name="TZB" s={sec.TZB} />}
        {sec.TPM && <RecapSection name="TPM" s={sec.TPM} />}
      </div>
    </div>
  );
}

function SignalCard({ c }) {
  const dot = c.accent === "red" ? "bg-red-500" : "bg-sky-500";
  return (
    <div className="ibc-sigcard w-full p-3.5" data-testid="signal-setup-card">
      <div className="flex items-start gap-2.5">
        <span className={`mt-1 h-3 w-3 rounded-full shrink-0 ${dot}`} />
        <div className="min-w-0">
          <div className="text-[15px] font-bold tracking-wide text-white uppercase leading-tight" data-testid="signal-setup-title">{c.title}</div>
          <div className="text-[11px] text-[#8A94A6] mt-0.5">{c.symbol} · {c.timeframe}</div>
        </div>
      </div>
      <div className="ibc-sigcard__divider my-2.5" />
      {c.entry != null && (
        <div className="flex items-baseline justify-between py-1">
          <span className="text-[11px] font-semibold tracking-widest uppercase text-[#E6C458]">Entry</span>
          <span className="text-sm font-bold text-white tabular-nums" data-testid="signal-setup-entry">{c.entry}</span>
        </div>
      )}
      {c.zone != null && (
        <div className="flex items-baseline justify-between py-1">
          <span className="text-[11px] font-semibold tracking-widest uppercase text-[#E6C458]">Zone</span>
          <span className="text-sm font-bold text-white tabular-nums" data-testid="signal-setup-zone">{c.zone}</span>
        </div>
      )}
      {c.sl_text && (
        <div className="flex items-baseline justify-between py-1 border-t border-white/[0.06]">
          <span className="text-[11px] font-semibold tracking-widest uppercase text-[#E6C458]">SL</span>
          <span className="text-sm font-semibold text-[#E8ECF4]" data-testid="signal-setup-sl">{c.sl_text}</span>
        </div>
      )}
      {(c.tp1 != null || c.tp2 != null) && (
        <div className="grid grid-cols-2 mt-2.5 ibc-sigcard__tps">
          <div className="text-center py-2">
            <div className="text-[10px] tracking-widest uppercase text-[#8A94A6]">TP1</div>
            <div className="text-sm font-bold text-white tabular-nums mt-0.5" data-testid="signal-setup-tp1">{c.tp1 ?? "—"}</div>
          </div>
          <div className="text-center py-2">
            <div className="text-[10px] tracking-widest uppercase text-[#8A94A6]">TP2</div>
            <div className="text-sm font-bold text-white tabular-nums mt-0.5" data-testid="signal-setup-tp2">{c.tp2 ?? "—"}</div>
          </div>
        </div>
      )}
      {c.disclaimer && <div className="text-center text-[10px] text-[#6B7486] mt-2.5">{c.disclaimer}</div>}
    </div>
  );
}

function TpCard({ c }) {
  const dot = c.accent === "red" ? "bg-red-500" : "bg-sky-500";
  return (
    <div className="ibc-tpcard w-full p-3.5" data-testid="signal-tp-card">
      <div className="flex items-start gap-2.5">
        <span className={`mt-1 h-3 w-3 rounded-full shrink-0 ${dot}`} />
        <div className="min-w-0">
          <div className="text-[15px] font-bold tracking-wide text-white uppercase leading-tight" data-testid="signal-tp-title">{c.title}</div>
          <div className="text-[11px] text-[#8A94A6] mt-0.5">{c.symbol} · {c.timeframe}</div>
        </div>
      </div>
      <div className="ibc-tpcard__divider my-2.5" />
      <div className="text-xl font-extrabold tracking-wide text-gold-gradient leading-none" data-testid="signal-tp-stage">HIT {c.stage}</div>
      <div className="text-[32px] font-black leading-none mt-1.5 text-emerald-400 ibc-tpcard__pips" data-testid="signal-tp-pips">{c.pips} PIPS</div>
      <div className="ibc-tpcard__target mt-3 flex items-center gap-2 px-3 py-2">
        <Target className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
        <span className="text-[11px] tracking-widest uppercase text-[#8A94A6]">{c.target_label}</span>
        <span className="ml-auto text-sm font-bold text-white tabular-nums" data-testid="signal-tp-target">{c.target}</span>
      </div>
    </div>
  );
}

function CardMeta({ m, mine, canDelete, onDelete, isPinned, onPin, onUnpin }) {
  return (
    <>
      <div className="flex items-center justify-end gap-1.5 mt-1 px-1 select-none">
        {typeof m.view_count === "number" && !m.pending && !m.failed && (
          <span className="inline-flex items-center gap-0.5 ibc-bubble__time" data-testid={`signal-msg-views-${m.id}`}>
            <Eye className="h-3 w-3" />{m.view_count}
          </span>
        )}
        <span className="ibc-bubble__time">{timeLabel(m.created_at)}</span>
        {mine && (
          m.pending ? <Loader2 className="h-3 w-3 text-[#64748B] animate-spin" />
          : m.failed ? <AlertCircle className="h-3 w-3 text-red-400" data-testid={`signal-msg-failed-${m.id}`} />
          : <Check className="h-3 w-3 text-[#D4AF37]" data-testid={`signal-msg-sent-${m.id}`} />
        )}
      </div>
      {canDelete && !m.legacy && !m.pending && !m.failed && (
        <div className="flex items-center justify-end gap-4 mt-1 px-1" data-testid={`signal-msg-actions-${m.id}`}>
          <button onClick={() => (isPinned ? onUnpin(m) : onPin(m))} className="text-[10px] text-[#D4AF37] hover:text-[#F4E3A1] inline-flex items-center gap-1" data-testid={`signal-msg-${isPinned ? "unpin" : "pin"}-${m.id}`}>
            <Pin className="h-3 w-3" />{isPinned ? "Unpin" : "Pin"}
          </button>
          <button onClick={() => onDelete(m)} className="text-[10px] text-red-400 hover:text-red-300 inline-flex items-center gap-1" data-testid={`signal-msg-delete-${m.id}`}>
            <Trash2 className="h-3 w-3" />Delete
          </button>
        </div>
      )}
    </>
  );
}

function MessageBubble({ m, onImageClick, mine, canDelete, onDelete, highlight, onRetry, onDiscard, isPinned, onPin, onUnpin, v2 = false, updates = [], highlightId, pinnedId }) {
  const meta = TYPE_META[m.message_type];
  if (m.message_type === "recap" && m.recap_data) {
    return (
      <div className="ibc-msg-in w-full" data-testid={`signal-msg-${m.id}`}>
        <RecapCard data={m.recap_data} />
      </div>
    );
  }
  if (m.card_data && (m.card_data.kind === "signal" || m.card_data.kind === "tp_result")) {
    const isTp = m.card_data.kind === "tp_result";
    const metaProps = { mine, canDelete, onDelete, onPin, onUnpin };
    let card;
    if (!v2) card = isTp ? <TpCard c={m.card_data} /> : <SignalCard c={m.card_data} />;
    else if (isTp) card = <IbUpdateCardV2 c={m.card_data} />;
    else card = (
      <IbEntryCardV2
        c={m.card_data} mid={m.id} updates={updates}
        focusId={updates.some((u) => u.id === highlightId) ? highlightId : null}
        renderUpdate={(u) => (
          <div className={u.id === highlightId ? "ibc-highlight" : ""} data-testid={`signal-msg-${u.id}`}>
            <IbUpdateCardV2 c={u.card_data} nested />
            <CardMeta m={u} isPinned={pinnedId === u.id} {...metaProps} />
          </div>
        )}
      />
    );
    return (
      <div className={`group flex flex-col ibc-msg-in ${mine ? "items-end" : "items-start"}`} data-testid={`signal-msg-${m.id}`}>
        <div className={`max-w-[92%] w-full ${highlight ? "ibc-highlight" : ""}`}>
          {card}
          <CardMeta m={m} isPinned={isPinned} {...metaProps} />
        </div>
      </div>
    );
  }
  return (
    <div className={`group flex flex-col ibc-msg-in ${mine ? "items-end" : "items-start"}`} data-testid={`signal-msg-${m.id}`}>
      <div className={`max-w-[86%] w-fit ${highlight ? "ibc-highlight" : ""}`}>
        <div className={`ibc-bubble ${mine ? "ibc-bubble--mine" : "ibc-bubble--other"}`}>
          {meta && (
            <span className={`inline-block text-[9px] font-mono tracking-widest uppercase border rounded-full px-2 py-0.5 mb-1.5 ${meta.cls}`} data-testid={`signal-msg-badge-${m.id}`}>
              {meta.label}
            </span>
          )}
          {m.has_image && m.thumb_url && (
            <button type="button" onClick={() => onImageClick(m)} className="block mb-2 overflow-hidden ibc-bubble__img w-full max-w-[280px]" data-testid={`signal-msg-thumb-${m.id}`}>
              <img src={mediaSrc(m.thumb_url)} alt="chart" loading="lazy" className="block w-full h-auto object-contain" />
            </button>
          )}
          {m.content && <p className="ibc-bubble__text text-left whitespace-pre-wrap break-words">{m.content}</p>}
          <div className="flex items-center justify-end gap-1.5 mt-1 select-none">
            {typeof m.view_count === "number" && !m.pending && !m.failed && (
              <span className="inline-flex items-center gap-0.5 ibc-bubble__time" data-testid={`signal-msg-views-${m.id}`}>
                <Eye className="h-3 w-3" />{m.view_count}
              </span>
            )}
            <span className="ibc-bubble__time">{timeLabel(m.created_at)}</span>
            {mine && (
              m.pending ? <Loader2 className="h-3 w-3 text-[#64748B] animate-spin" />
              : m.failed ? <AlertCircle className="h-3 w-3 text-red-400" data-testid={`signal-msg-failed-${m.id}`} />
              : <Check className="h-3 w-3 text-[#D4AF37]" data-testid={`signal-msg-sent-${m.id}`} />
            )}
          </div>
          {canDelete && !m.legacy && !m.pending && !m.failed && (
            <div className="flex items-center justify-end gap-4 mt-1.5 pt-1.5 border-t border-white/10" data-testid={`signal-msg-actions-${m.id}`}>
              <button onClick={() => (isPinned ? onUnpin(m) : onPin(m))} className="text-[10px] text-[#D4AF37] hover:text-[#F4E3A1] inline-flex items-center gap-1" data-testid={`signal-msg-${isPinned ? "unpin" : "pin"}-${m.id}`}>
                <Pin className="h-3 w-3" />{isPinned ? "Unpin" : "Pin"}
              </button>
              <button onClick={() => onDelete(m)} className="text-[10px] text-red-400 hover:text-red-300 inline-flex items-center gap-1" data-testid={`signal-msg-delete-${m.id}`}>
                <Trash2 className="h-3 w-3" />Delete
              </button>
            </div>
          )}
        </div>
      </div>
      {mine && m.failed && (
        <div className="flex items-center gap-3 mt-1 text-[10px]">
          <span className="text-red-400">Failed to send</span>
          <button onClick={() => onRetry(m)} className="inline-flex items-center gap-1 text-[#D4AF37] hover:text-[#F4E3A1]" data-testid={`signal-msg-retry-${m.id}`}>
            <RotateCw className="h-3 w-3" />Retry
          </button>
          <button onClick={() => onDiscard(m)} className="text-[#64748B] hover:text-red-300" data-testid={`signal-msg-discard-${m.id}`}>Discard</button>
        </div>
      )}
    </div>
  );
}

export default function SignalChannel({ signal, mode, onClose, onChanged, focusMessageId }) {
  const base = mode === "ib" ? `/ib/signals/${signal.id}` : `/member/activity/signals/${signal.id}`;
  const [cardV2, setCardV2] = useState(() => isIbCardV2Topic(signal));
  const v2 = cardV2;
  const [items, setItems] = useState([]);
  const renderItems = useMemo(() => (v2 ? groupIbSignalItems(items) : items.map((m) => ({ m, updates: [] }))), [items, v2]);
  const [hasMore, setHasMore] = useState(false);
  const [nextBefore, setNextBefore] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [ready, setReady] = useState(false);
  const [title, setTitle] = useState(signal.title || "");
  const [pair, setPair] = useState(signal.pair || "");
  const [ibName, setIbName] = useState(signal.ib_name || "");
  const [channelName, setChannelName] = useState(signal.channel_name || "");
  const [channelImage, setChannelImage] = useState(signal.channel_image_url ? mediaSrc(signal.channel_image_url) : null);
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editPreview, setEditPreview] = useState(null);
  const [editFile, setEditFile] = useState(undefined); // undefined=unchanged, null=remove, File=new
  const [savingProfile, setSavingProfile] = useState(false);
  const [contactLink, setContactLink] = useState(signal.contact_link || "");
  const [contactOpen, setContactOpen] = useState(false);
  const [contactInput, setContactInput] = useState("");
  const [savingContact, setSavingContact] = useState(false);
  const saveContact = async (clear = false) => {
    setSavingContact(true);
    try {
      const fd = new FormData();
      if (clear) fd.append("remove_contact", "true");
      else fd.append("contact_link", contactInput.trim());
      const r = await api.patch(`/ib/signals/${signal.id}/profile`, fd);
      setContactLink(r.data?.contact_link || "");
      setContactOpen(false);
      toast.success(clear ? "Contact link cleared" : "Contact link saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save contact link");
    } finally {
      setSavingContact(false);
    }
  };

  const onEditImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) { toast.error("Only JPG, PNG or WebP images are allowed"); return; }
    setEditFile(f);
    setEditPreview(URL.createObjectURL(f));
  };
  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const fd = new FormData();
      fd.append("name", editName);
      if (editFile instanceof File) fd.append("image", editFile);
      else if (editFile === null) fd.append("remove_image", "true");
      const r = await api.patch(`/ib/signals/${signal.id}/profile`, fd);
      setChannelName(r.data?.channel_name || "");
      setChannelImage(r.data?.channel_image_url ? mediaSrc(r.data.channel_image_url) : null);
      setEditOpen(false);
      toast.success("Channel updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update channel");
    } finally {
      setSavingProfile(false);
    }
  };
  const [disclosure, setDisclosure] = useState("");
  const [text, setText] = useState("");
  const [msgType, setMsgType] = useState("text");
  const [typeMenu, setTypeMenu] = useState(false);
  const [pending, setPending] = useState(null); // {file, previewUrl}
  const [sending, setSending] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [hasNew, setHasNew] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const [pinned, setPinned] = useState(null);
  const [seenCount, setSeenCount] = useState(null);
  const [vv, setVv] = useState(null); // {height, top} from visualViewport (mobile keyboard)
  const [isMobile, setIsMobile] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const inputRef = useRef(null);
  const seen = useRef(new Set());
  const payloads = useRef({});
  const sinceRef = useRef(new Date().toISOString());
  const scrollMode = useRef("follow"); // scroll intent: follow | reading | target
  const anchor = useRef(null);   // { id, offset } for reading/target preservation
  const programmatic = useRef(false);
  const viewed = useRef(new Set());
  const pendingViews = useRef(new Set());
  const viewTimer = useRef(null);

  const nodeTopWithin = (el, node) => node.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop;
  const setScrollTop = (top) => {
    const el = scrollRef.current; if (!el) return;
    programmatic.current = true; // so onScroll ignores geometry compensation
    el.scrollTop = top;
    requestAnimationFrame(() => { programmatic.current = false; });
  };
  const scrollToBottom = () => { const el = scrollRef.current; if (el) setScrollTop(el.scrollHeight); };
  const scrollToMessage = (id, center = true) => {
    const el = scrollRef.current; if (!el) return false;
    const node = el.querySelector(`[data-testid="signal-msg-${id}"]`); if (!node) return false;
    const within = nodeTopWithin(el, node);
    setScrollTop(Math.max(0, center ? within - (el.clientHeight / 2 - node.clientHeight / 2) : within - 8));
    anchor.current = { id, offset: node.getBoundingClientRect().top - el.getBoundingClientRect().top };
    return true;
  };
  const captureAnchor = () => {
    const el = scrollRef.current; if (!el) return;
    const elTop = el.getBoundingClientRect().top;
    const nodes = el.querySelectorAll('[data-testid^="signal-msg-"]');
    for (const n of nodes) {
      const off = n.getBoundingClientRect().top - elTop;
      if (off >= 0) { anchor.current = { id: n.getAttribute("data-testid").replace("signal-msg-", ""), offset: off }; break; }
    }
  };

  // keep the composer directly above the mobile keyboard using visualViewport
  useEffect(() => {
    setIsMobile(window.matchMedia("(max-width: 639px)").matches);
    const v = window.visualViewport;
    if (!v) return;
    const on = () => setVv({ height: v.height, top: v.offsetTop || 0 });
    on();
    v.addEventListener("resize", on);
    v.addEventListener("scroll", on);
    return () => { v.removeEventListener("resize", on); v.removeEventListener("scroll", on); };
  }, []);

  // scoped body-scroll lock while the chat is open (restored on close)
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const flushViews = useCallback(async () => {
    const ids = [...pendingViews.current].filter((id) => !viewed.current.has(id) && !String(id).startsWith("legacy") && !String(id).startsWith("tmp"));
    pendingViews.current = new Set();
    if (!ids.length) return;
    ids.forEach((id) => viewed.current.add(id));
    try {
      const { data } = await api.post(`${base}/views`, { message_ids: ids });
      const counts = data?.counts || {};
      setItems((prev) => prev.map((m) => (counts[m.id] != null ? { ...m, view_count: counts[m.id] } : m)));
    } catch { ids.forEach((id) => viewed.current.delete(id)); }
  }, [base]);

  // Register a view ONLY when a MEMBER actually sees a message in the active, visible
  // viewport (batched; deduped server-side per message+member). IB/admin never count;
  // kept fully separate from the unread/read-watermark logic.
  useEffect(() => {
    if (mode !== "member" || !ready) return;
    const el = scrollRef.current; if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (document.visibilityState !== "visible") return;
      let added = false;
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        const id = e.target.getAttribute("data-testid")?.replace("signal-msg-", "");
        if (id && !String(id).startsWith("legacy") && !String(id).startsWith("tmp") && !viewed.current.has(id)) { pendingViews.current.add(id); added = true; }
      }
      if (added) { clearTimeout(viewTimer.current); viewTimer.current = setTimeout(flushViews, 800); }
    }, { root: el, threshold: 0.6 });
    el.querySelectorAll('[data-testid^="signal-msg-"]').forEach((n) => io.observe(n));
    return () => { io.disconnect(); clearTimeout(viewTimer.current); };
  }, [mode, ready, items, flushViews]);

  // re-anchor on viewport (keyboard) changes: keep latest visible in FOLLOW,
  // preserve the reading/target message + pixel offset otherwise. Instant, not smooth.
  useLayoutEffect(() => {
    if (!ready) return;
    const el = scrollRef.current; if (!el) return;
    if (scrollMode.current === "follow") { scrollToBottom(); return; }
    if (anchor.current) {
      const node = el.querySelector(`[data-testid="signal-msg-${anchor.current.id}"]`);
      if (node) setScrollTop(Math.max(0, nodeTopWithin(el, node) - anchor.current.offset));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vv, ready]);

  const loadFirst = useCallback(async () => {
    try {
      const { data } = await api.get(`${base}/messages`, { params: { limit: 30 } });
      setTitle(data.signal?.title || signal.title || "");
      setPair(data.signal?.pair || signal.pair || "");
      setIbName(data.signal?.ib_name || signal.ib_name || "");
      // Adopt the saved channel profile from the shared read path (covers broadcast,
      // deep-link and reopen). Only overrides local state when the server provides it.
      if (data.signal) {
        setCardV2(isIbCardV2Topic({ ...data.signal, id: signal.id }));
        setChannelName(data.signal.channel_name || "");
        setChannelImage(data.signal.channel_image_url ? mediaSrc(data.signal.channel_image_url) : null);
        setContactLink(data.signal.contact_link || "");
      }
      setDisclosure(data.disclosure || "");
      const merged = [...(data.legacy || []), ...(data.messages || [])];
      seen.current = new Set(merged.map((m) => m.id));
      setItems(merged);
      setHasMore(data.has_more);
      setNextBefore(data.next_before);
      setReady(true);
      setPinned(data.pinned || null);
      if (mode === "ib" && typeof data.seen_count === "number") setSeenCount(data.seen_count);
      // Opening the channel marks the topic read server-side; refresh the Topics badge.
      if (mode === "member") window.dispatchEvent(new Event("pm-topics-unread-refresh"));
      const focusHit = focusMessageId && merged.some((m) => m.id === focusMessageId);
      requestAnimationFrame(() => {
        if (focusHit) {
          scrollMode.current = "target"; // deep-link target wins over default bottom scroll
          scrollToMessage(focusMessageId, true);
          setHighlightId(focusMessageId);
          setTimeout(() => setHighlightId(null), 2600);
        } else {
          scrollMode.current = "follow";
          scrollToBottom();
        }
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to load broadcast channel");
      setReady(true);
      // cross-tenant / missing topic → don't leave an empty modal shell open
      if (e.response?.status === 404 && onClose) onClose();
    }
  }, [base]); // eslint-disable-line react-hooks/exhaustive-deps

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get(`${base}/messages`, { params: { limit: 30 } });
      const fetched = data.messages || [];
      // In-place refresh for edited recaps already on screen (no scroll / unread change).
      setItems((prev) => prev.map((x) => {
        if (x.message_type !== "recap") return x;
        const f = fetched.find((fm) => fm.id === x.id);
        return f ? { ...x, ...f } : x;
      }));
      const additions = fetched.filter((m) => !seen.current.has(m.id));
      if (additions.length) {
        additions.forEach((m) => seen.current.add(m.id));
        setItems((prev) => [...prev, ...additions]);
        if (scrollMode.current === "follow") requestAnimationFrame(scrollToBottom); else setHasNew(true);
      }
    } catch { /* polling must never disrupt the view */ }
  }, [base]);

  const loadOlder = useCallback(async () => {
    if (!hasMore || loadingOlder || !nextBefore) return;
    setLoadingOlder(true);
    const el = scrollRef.current;
    const prevH = el ? el.scrollHeight : 0;
    try {
      const { data } = await api.get(`${base}/messages`, { params: { before: nextBefore, limit: 30 } });
      const older = [...(data.legacy || []), ...(data.messages || [])].filter((m) => !seen.current.has(m.id));
      older.forEach((m) => seen.current.add(m.id));
      setItems((prev) => [...older, ...prev]);
      setHasMore(data.has_more);
      setNextBefore(data.next_before);
      requestAnimationFrame(() => { if (el) setScrollTop(el.scrollHeight - prevH); });
    } catch { /* ignore */ } finally { setLoadingOlder(false); }
  }, [base, hasMore, loadingOlder, nextBefore]);

  const upsertLive = useCallback((m) => {
    if (!m || !m.id) return;
    if (m._edit) {
      // In-place edit (e.g. a settled daily recap): replace only if already on screen,
      // never scroll, never flag unread, never append out of place.
      setItems((prev) => (prev.some((x) => x.id === m.id) ? prev.map((x) => (x.id === m.id ? { ...x, ...m } : x)) : prev));
      return;
    }
    if (seen.current.has(m.id)) return;
    seen.current.add(m.id);
    setItems((prev) => [...prev, m]);
    if (scrollMode.current === "follow") requestAnimationFrame(scrollToBottom); else setHasNew(true);
  }, []);

  // Live delivery: SSE for the open topic; ~12s polling fallback if SSE fails.
  // The fallback poll is paused while the tab is hidden (SSE architecture untouched).
  useEffect(() => {
    loadFirst();
    let es = null;
    let pollTimer = null;
    const startPolling = () => { if (!pollTimer && !document.hidden) pollTimer = setInterval(poll, 12000); };
    const stopPolling = () => { if (pollTimer) { clearInterval(pollTimer); pollTimer = null; } };
    const onVis = () => {
      if (document.hidden) stopPolling();
      else if (!es) { poll(); startPolling(); } // resume only if we're in fallback mode (SSE closed)
    };
    try {
      es = new EventSource(`${API}${base}/stream?since=${encodeURIComponent(sinceRef.current)}`, { withCredentials: true });
      es.onmessage = (e) => { if (!e.data) return; try { upsertLive(JSON.parse(e.data)); } catch { /* keep-alive */ } };
      es.onerror = () => { if (es) { es.close(); es = null; } startPolling(); };
    } catch { startPolling(); }
    document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); if (es) es.close(); if (pollTimer) clearInterval(pollTimer); };
  }, [loadFirst, poll, base, upsertLive]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop < 60) loadOlder();
    if (programmatic.current) return; // ignore programmatic geometry compensation
    const distBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distBottom < 64) {
      scrollMode.current = "follow"; anchor.current = null;
      if (hasNew) setHasNew(false);
    } else {
      scrollMode.current = "reading"; // manual scroll releases target/follow pinning
      captureAnchor();
    }
  };

  const growInput = (el) => { if (!el) return; el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 120)}px`; };

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED.includes(file.type)) { toast.error("Only JPG, PNG or WebP images are allowed"); return; }
    const resized = await resizeImage(file, 1600);
    if (resized.size > 2 * 1024 * 1024) { toast.error("Image must be smaller than 2 MB after compression"); return; }
    setPending({ file: resized, previewUrl: URL.createObjectURL(resized) });
  };

  const doSend = async (tmpId) => {
    const p = payloads.current[tmpId];
    if (!p) return;
    setItems((prev) => prev.map((x) => (x.id === tmpId ? { ...x, pending: true, failed: false } : x)));
    setSending(true);
    try {
      let image_path = "";
      if (p.file) {
        const fd = new FormData();
        fd.append("file", p.file);
        const up = await api.post("/ib/signals/message-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        image_path = up.data.path;
      }
      const { data: real } = await api.post(`${base}/messages`, { content: p.text, message_type: p.type, image_path });
      seen.current.add(real.id); // prevents SSE from re-adding this message
      // Reconcile robustly against the SSE-vs-optimistic race: if the live stream
      // already delivered `real` (common for images because the upload adds an RTT),
      // drop that copy first, then swap the optimistic tmp bubble for the canonical one.
      setItems((prev) => prev.filter((x) => x.id !== real.id).map((x) => (x.id === tmpId ? real : x)));
      if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      delete payloads.current[tmpId];
      scrollMode.current = "follow";
      requestAnimationFrame(scrollToBottom);
      onChanged && onChanged();
    } catch (e) {
      setItems((prev) => prev.map((x) => (x.id === tmpId ? { ...x, pending: false, failed: true } : x)));
      toast.error(e.response?.data?.detail || "Failed to send — tap retry");
    } finally { setSending(false); }
  };

  const startSend = () => {
    if (!text.trim() && !pending) { toast.error("Add a message or attach an image"); return; }
    const tmpId = `tmp-${Date.now()}`;
    payloads.current[tmpId] = { text: text.trim(), type: msgType, file: pending?.file || null, previewUrl: pending?.previewUrl || null };
    setItems((prev) => [...prev, {
      id: tmpId, message_type: msgType, content: text.trim(), result_status: null,
      created_at: new Date().toISOString(), has_image: !!pending, pending: true,
    }]);
    requestAnimationFrame(scrollToBottom);
    setText(""); setMsgType("text"); setPending(null);
    if (inputRef.current) inputRef.current.style.height = "auto";
    scrollMode.current = "follow";
    doSend(tmpId);
  };

  const discard = (m) => {
    const p = payloads.current[m.id];
    if (p?.previewUrl) URL.revokeObjectURL(p.previewUrl);
    delete payloads.current[m.id];
    setItems((prev) => prev.filter((x) => x.id !== m.id));
  };

  const jumpToPinned = async (id) => {
    scrollMode.current = "target";
    for (let i = 0; i < 20; i++) {
      if (scrollToMessage(id, true)) { setHighlightId(id); setTimeout(() => setHighlightId(null), 2600); return; }
      if (!hasMore) break;
      const before = seen.current.size;
      await loadOlder();
      await new Promise((r) => setTimeout(r, 50));
      if (seen.current.size === before) break;
    }
    if (scrollToMessage(id, true)) { setHighlightId(id); setTimeout(() => setHighlightId(null), 2600); }
  };

  const doPin = async (m) => {
    try { await api.post(`${base}/pin`, { message_id: m.id }); setPinned(m); toast.success("Message pinned"); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to pin"); }
  };
  const doUnpin = async () => {
    try { await api.delete(`${base}/pin`); setPinned(null); toast.success("Message unpinned"); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to unpin"); }
  };

  const remove = async (m) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`${base}/messages/${m.id}`);
      seen.current.delete(m.id);
      setItems((prev) => prev.filter((x) => x.id !== m.id));
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const hideTopic = async () => {
    if (!window.confirm("Clear this chat? Older messages will be hidden from your view. New messages from your IB will still appear.")) return;
    try {
      await api.post(`${base}/hide`);
      toast.success("Chat cleared");
      onChanged && onChanged();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to clear chat"); }
  };

  const activeType = COMPOSER_TYPES.find(([v]) => v === msgType) || COMPOSER_TYPES[0];
  const panelStyle = isMobile && vv ? { height: `${Math.round(vv.height)}px`, transform: `translateY(${Math.round(vv.top)}px)` } : undefined;

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center sm:items-center sm:p-4" onClick={onClose}>
      <div
        style={panelStyle}
        className="relative w-full h-[100dvh] sm:h-[85vh] sm:max-w-lg card-lux flex flex-col overflow-hidden sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="signal-channel"
      >
        {/* Channel header */}
        <div className="shrink-0 flex items-center gap-2 px-3 py-2.5 border-b border-white/10 bg-[#0B0B0F]" style={{ paddingTop: "max(env(safe-area-inset-top), 0.625rem)" }}>
          <button onClick={onClose} className="sm:hidden -ml-1 text-[#94A3B8] hover:text-white transition-colors" data-testid="signal-channel-back" aria-label="Back">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <Avatar name={channelName || ibName} image={channelImage} size="h-9 w-9 text-[11px]" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-white truncate uppercase tracking-wide" data-testid="signal-channel-title">{channelName || title}</span>
              {pair && <span className="shrink-0 text-[10px] font-mono tracking-widest text-[#D4AF37] border border-[#D4AF37]/30 bg-[#D4AF37]/10 rounded-full px-2 py-0.5">{pair}</span>}
            </div>
            <p className="text-[11px] text-[#64748B] flex items-center gap-1.5 truncate">
              <Radio className="h-3 w-3 text-emerald-400 shrink-0" />{ibName || "PLUSMAKER IB"} · Broadcast channel
            </p>
          </div>
          {mode === "ib" && (
            <button
              onClick={() => { setEditName(channelName || ""); setEditPreview(channelImage); setEditFile(undefined); setEditOpen(true); }}
              className="text-[#94A3B8] hover:text-[#D4AF37] transition-colors"
              data-testid="signal-channel-edit"
              title="Edit Channel"
              aria-label="Edit Channel"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
          {mode === "member" && (
            <button onClick={hideTopic} className="text-[#94A3B8] hover:text-red-400 transition-colors" data-testid="signal-channel-hide" title="Clear chat">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <button onClick={onClose} className="text-[#94A3B8] hover:text-white transition-colors" data-testid="signal-channel-close" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {editOpen && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/70 p-4" data-testid="edit-channel-modal" onClick={() => !savingProfile && setEditOpen(false)}>
            <div className="w-full max-w-sm card-lux p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-white">Edit Channel</p>
                <button onClick={() => setEditOpen(false)} className="text-[#94A3B8] hover:text-white" data-testid="edit-channel-close" aria-label="Close"><X className="h-4 w-4" /></button>
              </div>
              <div className="flex items-center gap-3">
                <Avatar name={editName || ibName} image={editPreview} size="h-14 w-14 text-sm" />
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-[#D4AF37] hover:text-[#F4E3A1] cursor-pointer inline-flex items-center gap-1.5" data-testid="edit-channel-image-label">
                    <ImagePlus className="h-3.5 w-3.5" /> {editPreview ? "Change image" : "Upload image"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onEditImage} data-testid="edit-channel-image-input" />
                  </label>
                  {editPreview && (
                    <button onClick={() => { setEditFile(null); setEditPreview(null); }} className="text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1.5 text-left" data-testid="edit-channel-image-remove">
                      <Trash2 className="h-3.5 w-3.5" /> Remove image
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">Channel name</label>
                <input value={editName} onChange={(e) => setEditName(e.target.value)} maxLength={60} placeholder={ibName || "Broadcast"} className="input-lux w-full mt-1" data-testid="edit-channel-name-input" />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => setEditOpen(false)} disabled={savingProfile} className="flex-1 py-2 text-sm rounded-lg border border-[#222530] text-[#94A3B8] hover:text-white transition-colors" data-testid="edit-channel-cancel">Cancel</button>
                <button onClick={saveProfile} disabled={savingProfile} className="flex-1 btn-gold py-2 text-sm rounded-lg disabled:opacity-50" data-testid="edit-channel-save">{savingProfile ? "Saving…" : "Save"}</button>
              </div>
            </div>
          </div>
        )}

        {contactOpen && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/70 p-4" data-testid="contact-link-modal" onClick={() => !savingContact && setContactOpen(false)}>
            <div className="w-full max-w-sm card-lux p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-white">Contact Link</p>
                <button onClick={() => setContactOpen(false)} className="text-[#94A3B8] hover:text-white" data-testid="contact-link-close" aria-label="Close"><X className="h-4 w-4" /></button>
              </div>
              <p className="text-[11px] text-[#64748B] leading-relaxed">Add one Telegram or social link (https only). Clients will see a “Contact {ibName || "you"}” button in the channel footer.</p>
              <input value={contactInput} onChange={(e) => setContactInput(e.target.value)} maxLength={300} placeholder="https://t.me/yourhandle" className="input-lux w-full" data-testid="contact-link-input" />
              <div className="flex gap-2 pt-1">
                {contactLink && <button onClick={() => saveContact(true)} disabled={savingContact} className="py-2 px-3 text-sm rounded-lg border border-red-500/30 text-red-400 hover:text-red-300" data-testid="contact-link-clear">Clear</button>}
                <button onClick={() => setContactOpen(false)} disabled={savingContact} className="flex-1 py-2 text-sm rounded-lg border border-[#222530] text-[#94A3B8] hover:text-white" data-testid="contact-link-cancel">Cancel</button>
                <button onClick={() => saveContact(false)} disabled={savingContact} className="flex-1 btn-gold py-2 text-sm rounded-lg disabled:opacity-50" data-testid="contact-link-save">{savingContact ? "Saving…" : "Save"}</button>
              </div>
            </div>
          </div>
        )}

        {pinned && (
          <button onClick={() => jumpToPinned(pinned.id)} className="ibc-pinned shrink-0 flex items-center gap-2 px-3 py-2 transition-colors text-left" data-testid="signal-pinned-banner">
            <Pin className="h-3.5 w-3.5 text-[#D4AF37] shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-mono tracking-widest uppercase text-[#D4AF37]">Pinned</p>
              <p className="text-xs text-[#E2E8F0] truncate" data-testid="signal-pinned-preview">{pinned.content || (pinned.has_image ? "[Image]" : "")}</p>
            </div>
            {mode === "ib" && (
              <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); doUnpin(); }} className="text-[10px] text-[#94A3B8] hover:text-red-400 shrink-0" data-testid="signal-pinned-unpin">Unpin</span>
            )}
            <ChevronRight className="h-4 w-4 text-[#D4AF37]/70 shrink-0" />
          </button>
        )}

        {/* Message stream — bottom-anchored */}
        <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto" data-testid="signal-channel-stream">
          <div className="min-h-full flex flex-col justify-end gap-2.5 px-4 py-4">
            {!ready && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 text-[#D4AF37] animate-spin" /></div>}
            {ready && loadingOlder && <div className="flex justify-center py-2"><Loader2 className="h-4 w-4 text-[#64748B] animate-spin" /></div>}
            {ready && items.length === 0 && <p className="text-sm text-[#64748B] text-center py-10">No messages yet.</p>}
            {renderItems.map(({ m, updates }, i) => {
              const prev = renderItems[i - 1]?.m;
              const showSep = !prev || dateKey(prev.created_at) !== dateKey(m.created_at);
              return (
                <div key={m.id}>
                  {showSep && <DateSeparator label={dateLabel(m.created_at)} />}
                  <MessageBubble
                    m={m}
                    v2={v2}
                    updates={updates}
                    highlightId={highlightId}
                    pinnedId={pinned?.id}
                    mine={mode === "ib"}
                    highlight={m.id === highlightId}
                    onImageClick={(msg) => setLightbox(mediaSrc(msg.image_url))}
                    canDelete={mode === "ib"}
                    onDelete={remove}
                    isPinned={pinned?.id === m.id}
                    onPin={doPin}
                    onUnpin={doUnpin}
                    onRetry={(x) => doSend(x.id)}
                    onDiscard={discard}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {hasNew && (
          <button
            onClick={() => { scrollMode.current = "follow"; scrollToBottom(); setHasNew(false); }}
            className="ibc-newpill absolute left-1/2 -translate-x-1/2 bottom-28 z-10 flex items-center gap-1 text-[11px] font-medium text-[#0B0B0F] bg-[#D4AF37] rounded-full px-3 py-1.5 shadow-[0_6px_20px_rgba(0,0,0,0.4)]"
            data-testid="signal-new-message-pill"
          >
            <ChevronDown className="h-3.5 w-3.5" />New messages
          </button>
        )}

        {/* Footer: composer (IB) or read-only notice (member) */}
        {mode === "ib" ? (
          <div className="shrink-0 border-t border-white/10 bg-[#0B0B0F]/90 px-3 pt-2.5 pb-3" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }}>
            <div className="relative flex items-center gap-2 mb-2">
              <button onClick={() => setTypeMenu((o) => !o)} className={`inline-flex items-center gap-1 text-[10px] font-mono tracking-widest uppercase border rounded-full px-2.5 py-1 transition-colors ${msgType === "text" ? "text-[#94A3B8] border-white/15" : (TYPE_META[msgType]?.cls || "text-white border-white/20")}`} data-testid="signal-composer-typemenu">
                {activeType[1]} <ChevronDown className={`h-3 w-3 transition-transform ${typeMenu ? "rotate-180" : ""}`} />
              </button>
              <button onClick={() => { setContactInput(contactLink || ""); setContactOpen(true); }} className="inline-flex items-center gap-1 text-[10px] font-mono tracking-widest uppercase border rounded-full px-2.5 py-1 transition-colors text-[#94A3B8] border-white/15 hover:text-[#D4AF37] hover:border-[#D4AF37]/40" data-testid="signal-composer-contact" title="Contact link">
                <Link2 className="h-3 w-3" />{contactLink ? "LINK" : "LINK +"}
              </button>
              {pending && (
                <div className="flex items-center gap-2 ml-auto" data-testid="signal-composer-preview">
                  <img src={pending.previewUrl} alt="preview" className="h-8 w-8 rounded-md object-cover border border-white/10" />
                  <button onClick={() => { URL.revokeObjectURL(pending.previewUrl); setPending(null); }} className="text-[10px] text-red-400 hover:text-red-300">Remove</button>
                </div>
              )}
              {typeMenu && (
                <div className="absolute bottom-full left-0 mb-2 z-20 w-40 rounded-xl border border-white/10 bg-[#12121A] shadow-[0_10px_30px_rgba(0,0,0,0.5)] overflow-hidden" data-testid="signal-composer-typelist">
                  {COMPOSER_TYPES.map(([v, label, color]) => (
                    <button
                      key={v}
                      onClick={() => { setMsgType(v); setTypeMenu(false); }}
                      className={`w-full text-left px-3 py-2 text-[11px] font-mono tracking-widest uppercase transition-colors hover:bg-white/5 ${v === msgType ? "bg-white/[0.06]" : ""} ${color}`}
                      data-testid={`signal-type-${v}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-end gap-2">
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pickImage} data-testid="signal-composer-file" />
              <button onClick={() => fileRef.current?.click()} className="h-10 w-10 shrink-0 rounded-full border border-white/10 text-[#94A3B8] hover:text-[#D4AF37] hover:border-[#D4AF37]/40 flex items-center justify-center transition-colors" data-testid="signal-composer-attach" aria-label="Attach image">
                <ImagePlus className="h-4 w-4" />
              </button>
              <textarea
                ref={inputRef}
                value={text}
                onChange={(e) => { setText(e.target.value); growInput(e.target); }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); startSend(); } }}
                rows={1}
                placeholder="Broadcast a message…"
                className="input-lux signal-composer-textarea flex-1 resize-none max-h-[120px] py-2.5 leading-snug"
                data-testid="signal-composer-input"
              />
              <button onClick={startSend} disabled={sending} className="btn-gold h-11 w-11 shrink-0 rounded-full flex items-center justify-center p-0 disabled:opacity-60" data-testid="signal-composer-send" aria-label="Send">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </div>
        ) : (
          <div className="shrink-0 border-t border-white/10 bg-[#0B0B0F]/90 px-4 py-2" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }}>
            <p className="text-[11px] text-[#64748B] flex items-center justify-center gap-1.5" data-testid="signal-channel-readonly">
              <ShieldCheck className="h-3.5 w-3.5 text-[#D4AF37]" />Read-only broadcast channel
            </p>
            {contactLink && /^https:\/\//i.test(contactLink) && (
              <a href={contactLink} target="_blank" rel="noopener noreferrer nofollow" className="mt-1.5 mx-auto flex w-fit items-center gap-1.5 text-[11px] text-[#D4AF37] hover:text-[#F4E3A1] border border-[#D4AF37]/30 bg-[#D4AF37]/10 rounded-full px-3 py-1 transition-colors" data-testid="signal-contact-ib">
                <Link2 className="h-3 w-3" />Contact {ibName || "your IB"}
              </a>
            )}
          </div>
        )}
      </div>

      {/* Lightbox for full image */}
      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); setLightbox(null); }} data-testid="signal-lightbox">
          <img src={lightbox} alt="chart full" className="max-h-[90vh] max-w-full rounded-lg" />
          <button onClick={(e) => { e.stopPropagation(); setLightbox(null); }} className="absolute top-4 right-4 text-white/80 hover:text-white"><X className="h-6 w-6" /></button>
        </div>
      )}
    </div>,
    document.body
  );
}
